// src/asset-delivery/resolver/AssetResolver.ts

import type { PlaybackItem, PlaybackKind, ResolvedPlayback } from "../orchestrator/types";
import {
  fetchVideoPlaybackURL,
  fetchAudioPlaybackURL,
  fetchImagePlaybackURL,
  fetchThumbnailPlaybackURL,
  fetchFilePlaybackURL, // +file
} from "../api";

type ResolveOptions = {
  forceRefresh?: boolean;
};

export class AssetResolver {
  private cache = new Map<string, { value: ResolvedPlayback; exp: number }>();
  private inflight = new Map<string, Promise<ResolvedPlayback>>();

  // Safety margin before URL expiry (ms)
  private safetyMs = 30_000;

  private cacheKey(item: PlaybackItem): string {
    return item.id;
  }

  private getFromCache(key: string): ResolvedPlayback | null {
    const hit = this.cache.get(key);
    if (!hit) return null;

    if (Date.now() > hit.exp) {
      this.cache.delete(key);
      return null;
    }
    return hit.value;
  }

  private computeExpiry(res: ResolvedPlayback): number {
    if (res.expiresAt) {
      const ts = Date.parse(res.expiresAt);
      if (!Number.isNaN(ts)) return ts - this.safetyMs;
    }
    return Date.now() + 60_000;
  }

  async resolve(item: PlaybackItem, opts?: ResolveOptions): Promise<ResolvedPlayback> {
    const key = this.cacheKey(item);

    // Force refresh: drop cache + inflight
    if (opts?.forceRefresh) {
      this.cache.delete(key);
      this.inflight.delete(key);
    }

    const cached = this.getFromCache(key);
    if (cached) return cached;

    const inflight = this.inflight.get(key);
    if (inflight) return inflight;

    const p = (async () => {
      // Default fieldName matches type (file -> "file")
      const fieldName = item.fieldName ?? item.type;
      let res: ResolvedPlayback;

      switch (item.type as PlaybackKind) {
        case "video":
          res = await fetchVideoPlaybackURL(item.target, fieldName);
          break;
        case "audio":
          res = await fetchAudioPlaybackURL(item.target, fieldName);
          break;
        case "image":
          res = await fetchImagePlaybackURL(item.target, fieldName);
          break;
        case "thumbnail":
          res = await fetchThumbnailPlaybackURL(item.target, fieldName);
          break;
        case "file":
          res = await fetchFilePlaybackURL(item.target, fieldName);
          break;
        default:
          throw new Error(`Unsupported playback type: ${item.type}`);
      }

      const exp = this.computeExpiry(res);
      this.cache.set(key, { value: res, exp });
      return res;
    })();

    this.inflight.set(key, p);

    try {
      return await p;
    } finally {
      this.inflight.delete(key);
    }
  }

  invalidateAll() {
    this.cache.clear();
    this.inflight.clear();
  }
}
